import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from 'yup';
import { CriticalityBadge } from "../../../../components/Badges/CriticalityBadge";
import { CriticalityScoreBadge } from "../../../../components/Badges/CriticalityScoreBadge";
import { HostingsiteBadge } from "../../../../components/Badges/HostingsiteBadge";
import { OrganisationBadge } from "../../../../components/Badges/OrganisationBadge";
import { PropagationBadge } from "../../../../components/Badges/PropagationBadge";
import { ServiceBadge } from "../../../../components/Badges/ServiceBadge";
import { ServiceCategoryBadge } from "../../../../components/Badges/ServiceCategoryBadge";
import EasyForm, { FormType } from "../../../../components/Forms/EasyForm";
import EasyFormColumn from "../../../../components/Forms/EasyFormColumn";
import { AutocompleteFieldRender } from "../../../../components/Forms/Renders/AutocompleteFieldRender";
import { CheckboxFieldRender } from "../../../../components/Forms/Renders/CheckboxFieldRender";
import { TextFieldRender } from "../../../../components/Forms/Renders/TextFieldRender";
import { TextRenderer } from "../../../../components/Forms/Renders/TextRenderer";
import TooltipComponent from "../../../../components/TooltipComponent";
import Loading from "../../../../components/loading";
import { PaginationQueryPackage } from "../../../../models/API/QueryParams/PaginationQueryPackage";
import HostingsiteBase from "../../../../models/Hostingsite";
import OrganisationResponse from "../../../../models/Organisation";
import { ServiceEndpoint, ServiceResponseDetails } from "../../../../models/Service";
import { ServiceTagResponse } from "../../../../models/ServiceTag";
import { CriticalityEnum } from "../../../../models/enums/CriticalityEnum";
import { PropagationEnum } from "../../../../models/enums/PropagationEnum";
import { ServiceCategoryEnum } from "../../../../models/enums/ServiceCategoryEnum";
import { PermissionEnum } from "../../../Permission/PermissionEnum";
import { useGetAllPortaliasQuery } from "../../../PortaliasApi/redux/portaliasApiSlice";
import { AbilityContext } from "../../../UserApi/logic/Can";
import useServiceTag from "../../Hooks/serviceTagHook";
import ServiceTagFieldRender from "../../Renders/ServiceTagFieldRender";
import { useDeleteEndpointMutation, useForceUpdateEndpointMutation, useGetEndpointsByServiceIdQuery } from "../../redux/endpointApiSlice";
import { useGetServiceByIdQuery, useUpdateServiceMutation } from "../../redux/serviceApiSlice";
import { Stack, Typography } from "@mui/material";

export default function InfoTab(props: { serviceId: string }) {
    const [newlyCreatedServiceTag, setNewlyCreatedServiceTag] = useState<ServiceTagResponse | undefined>(undefined)

    const ability = useContext(AbilityContext);
    const serviceResult = useGetServiceByIdQuery(props.serviceId)
    const endpointsResult = useGetEndpointsByServiceIdQuery({
        pagination: new PaginationQueryPackage(),
        uuid: props.serviceId
    })
    const portaliasesResult = useGetAllPortaliasQuery({ pagination: new PaginationQueryPackage() })
    const updateEndpoint = useForceUpdateEndpointMutation()[0]
    const deleteEndpoint = useDeleteEndpointMutation()[0]

    const { t } = useTranslation()
    const serviceTags = useServiceTag(serviceResult.data?.hosting_site)

    if (serviceResult.isLoading || endpointsResult.isLoading || portaliasesResult.isLoading)
        return <Loading />

    const icmpPortalias = portaliasesResult.data?.find(portalias => portalias.name === "ICMP")
    const endpointsWithICMP = endpointsResult.data?.filter(endpoint => endpoint.ports.map(port => port.name).includes("ICMP")) ?? []
    const endpointsWithICMPOnly = endpointsWithICMP.filter(endpoint => endpoint.ports.length === 1)
    const endpointsWithoutICMP = endpointsResult.data?.filter(endpoint => !endpoint.ports.map(port => port.name).includes("ICMP")) ?? []

    const SignupSchema = Yup.object().shape({
        name: Yup.string().required(t('Required')),
        sla: Yup.number().nullable().required(t('Required')).min(0).max(100),
        hosting_site: Yup.object().nullable().required(),
        servicetag: Yup.mixed().nullable(),
        uses_encryption: Yup.boolean().default(false).required(),
        no_encryption_reason: Yup.string().when('uses_encryption', {
            is: (uses_encryption: boolean) => uses_encryption == false,
            then: Yup.string().required(t('Required'))
        }),
        criticality: Yup.string().nullable().required(t("Required")),
        propagation: Yup.string().nullable().required(t("Required")),
    });

    const getFirstRow = () => {
        return [
            new TextRenderer({
                displayText: "ID",
                fieldName: "short_id",
                uniqueId: props.serviceId
            }),
            new TextFieldRender({
                displayText: "Name",
                fieldName: "name",
                type: "text",
                render: (content) => <ServiceBadge textOverride={content as string} />,
                uniqueId: props.serviceId
            }),
            new TextRenderer({
                displayText: "Organisation",
                fieldName: "hosting_site.owner_organisation",
                uniqueId: props.serviceId,
                render: (content) => <OrganisationBadge ressource={content as unknown as OrganisationResponse} />
            }),
            new TextRenderer({
                displayText: "Hostingsite",
                fieldName: "hosting_site",
                render: (option) => <HostingsiteBadge ressource={option as HostingsiteBase} />,
                uniqueId: props.serviceId
            }),
            ServiceTagFieldRender({
                translation: t,
                serviceTags: serviceTags,
                setNewlyCreatedServiceTag: setNewlyCreatedServiceTag,
                newlyCreatedServiceTag: newlyCreatedServiceTag,
                hostingSite: serviceResult.data?.hosting_site
            }),
            new TextFieldRender({
                displayText: "Description",
                fieldName: "description",
                type: "text",
                uniqueId: props.serviceId,
                multiline: true
            }),
            new TextFieldRender({
                displayText: "Internal description",
                fieldName: "internal_description",
                type: "text",
                uniqueId: props.serviceId,
                multiline: true
            }),
            new CheckboxFieldRender({
                displayText: "Requires approval",
                fieldName: "requires_approval",
                uniqueId: props.serviceId
            }),
            new TextFieldRender({
                displayText: "SLA",
                fieldName: "sla",
                type: "number",
                render: (content) => <>{content} %</>,
                endAdornment: "%",
                uniqueId: props.serviceId
            }),
            new TextFieldRender({
                displayText: "SLA description",
                fieldName: "sla_description",
                type: "text",
                uniqueId: props.serviceId
            }),
            new CheckboxFieldRender({
                displayText: "Uses encryption",
                fieldName: "uses_encryption",
                uniqueId: props.serviceId
            }),
            new TextFieldRender({
                displayText: "Reason for no encryption",
                fieldName: "no_encryption_reason",
                type: "text",
                multiline: true,
                uniqueId: "no_id",
                conditionFieldName: "uses_encryption",
                conditionShouldBeDisabledFunc: (uses_encryption: boolean) => uses_encryption == true
            }),
            new TextFieldRender({
                displayText: "Service window rules",
                fieldName: "service_window_rules",
                type: "text",
                uniqueId: props.serviceId
            }),
            new TextFieldRender({
                displayText: "General contact information",
                fieldName: "general_contact_information",
                multiline: true,
                type: "text",
                uniqueId: props.serviceId
            }),
            new CheckboxFieldRender({
                displayText: "ICMP (Ping)",
                fieldName: "icmp",
                uniqueId: props.serviceId,
                render: (checked) => checked === undefined
                    ? <TooltipComponent title={t("On x out of y endpoints", { "x": endpointsWithICMP?.length, "y": endpointsResult.data?.length })}>{t("Partially")}</TooltipComponent>
                    : checked ? t("Yes") : t("No"),
                disabled: icmpPortalias === undefined || endpointsResult.data?.length === 0,
                disabledTooltip: icmpPortalias === undefined ? t("ICMP portalias does not exist") : t("Service has no endpoints")
            }),
        ];
    }

    const getCriticalityRow = () => {
        return [
            new TextRenderer({
                displayText: "Criticality score",
                fieldName: "criticality_score",
                uniqueId: props.serviceId,
                render: (content) => <CriticalityScoreBadge textOverride={content as string} />
            }),
            new AutocompleteFieldRender({
                displayText: t("Criticality"),
                fieldName: "criticality",
                getOptionLabelFunc: (option: CriticalityEnum) => option.toString() ?? "",
                options: Object.values(CriticalityEnum) ?? [],
                renderNonChangeable: (content) => <CriticalityBadge ressource={content as CriticalityEnum} />,
                selectedValueInOptions: (selected, options) => options.includes(selected),
                uniqueId: "no_id"
            }),
            new AutocompleteFieldRender({
                displayText: t("Propagation"),
                fieldName: "propagation",
                getOptionLabelFunc: (option: PropagationEnum) => option.toString() ?? "",
                options: Object.values(PropagationEnum) ?? [],
                renderNonChangeable: (content) => <PropagationBadge ressource={content as PropagationEnum} />,
                selectedValueInOptions: (selected, options) => options.includes(selected),
                uniqueId: "no_id"
            }),
            new AutocompleteFieldRender({
                displayText: t("Categories"),
                fieldName: "service_categories",
                multipleSelect: true,
                getOptionLabelFunc: (option: ServiceCategoryEnum) => option.toString() ?? "",
                renderNonChangeable: (option: ServiceCategoryEnum) => <ServiceCategoryBadge ressource={option} />,
                options: Object.values(ServiceCategoryEnum) ?? [],
                uniqueId: "no_id"
            }),

        ];
    }

    const enrichedService = { ...serviceResult.data } as EnrichedService
    enrichedService.icmp = endpointsWithICMP?.length === 0 ? false
        : endpointsWithICMP?.length === endpointsResult.data?.length ? true : undefined

    const runAsyncEndpointActions = async (endpoints: ServiceEndpoint[], action: (endpoint: ServiceEndpoint) => Promise<void>) => {
        for (const endpoint of endpoints) {
            await action(endpoint)
        }
    }

    const addICMP = async (endpoint: ServiceEndpoint) => {
        await updateEndpoint({
            data: { ...endpoint, ports: [...endpoint.ports, icmpPortalias!] }
        }).unwrap()
    }
    const removeICMP = async (endpoint: ServiceEndpoint) => {
        const nonICMPPorts = endpoint.ports.filter(port => port.name !== "ICMP")
        nonICMPPorts.length === 0
            ? await deleteEndpoint({ data: endpoint }).unwrap()
            : await updateEndpoint({ data: { ...endpoint, ports: nonICMPPorts } }).unwrap()
    }

    return (
        <EasyForm<ServiceResponseDetails>
            isAllowedToEdit={(serviceResponse) => ability.can(PermissionEnum.SERVICE_WRITE, serviceResponse)}
            saveMutation={useUpdateServiceMutation}
            additionalSaveActions={(request) => {
                const enrichedRequest = request?.data as EnrichedService
                return enrichedRequest?.icmp === undefined ? undefined :
                    enrichedRequest.icmp ? runAsyncEndpointActions(endpointsWithoutICMP, addICMP) :
                        runAsyncEndpointActions(endpointsWithICMP, removeICMP)
            }}
            initialValue={enrichedService}
            arrayPath=""
            SignupSchema={SignupSchema}
            columns={[
                new EasyFormColumn("Service information", "", getFirstRow()),
                new EasyFormColumn("Criticality information", "", getCriticalityRow()),
            ]}
            getFieldRows={() => []}
            newButtonTooltip="Add new info"
            formType={FormType.INFORMATION}
            createNew={function () {
                throw new Error("Function not implemented.");
            }}
            key={enrichedService.uuid}
            saveConfirmationBody={(request) => {
                const enrichedRequest = request as EnrichedService
                return enrichedRequest.icmp === false && endpointsWithICMPOnly.length > 0 ?
                    <Stack>
                        <Typography>{t("x endpoint(s) will be deleted.", { "x": endpointsWithICMPOnly.length })}</Typography>
                        <Typography>{t("Are you sure you wish to remove ICMP (Ping)?")}</Typography>
                    </Stack>
                    : undefined
            }}
        />
    )
}

class EnrichedService extends ServiceResponseDetails {
    icmp?: boolean;
}
